import type {
  DashboardSales,
  DashboardShare,
  DashboardTopProduct,
  OutletSummary,
  OwnerDashboardView,
} from "@kopi/contracts";
import { reportDays } from "@kopi/contracts/client";
import { addDays, businessDate } from "@kopi/domain";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Card, CardContent, CardHeader } from "@kopi/ui/components/card";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { StatCard } from "@kopi/ui/components/stat-card";
import { cn } from "@kopi/ui/lib/utils";
import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import {
  ArrowDownRightIcon,
  ArrowUpRightIcon,
  BookOpenIcon,
  ChartColumnIcon,
  ChevronRightIcon,
  ClockIcon,
  CreditCardIcon,
  MinusIcon,
  MonitorSmartphoneIcon,
  PackageIcon,
  PiggyBankIcon,
  ReceiptTextIcon,
  RefreshCwIcon,
  ScaleIcon,
  SettingsIcon,
  ShoppingBasketIcon,
  StoreIcon,
  TrophyIcon,
  UsersIcon,
  WalletIcon,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import {
  type ChartBucket,
  ColumnChart,
  FigureCard,
  figureSpan,
  hourBuckets,
  ListSkeleton,
  MixBreakdown,
  PanelCard,
  RankedList,
  type Slice,
  type Tone,
} from "@/components/sales-visuals";
import { INTL_LOCALE } from "@/i18n/locale";
import { dashboard } from "@/i18n/messages/dashboard";
import { useMessages } from "@/i18n/provider";
import { canAnywhere, canAt, canTenantWide } from "@/lib/access";
import { api, unwrap } from "@/lib/api";
import { canOpenSettings } from "@/lib/nav";
import { meScopesQuery, outletsQuery } from "@/lib/queries";
import { calendarDay, isoDay, isoInstant } from "@/lib/report-dates";

export const Route = createFileRoute("/_authed/")({
  staticData: { section: "workspace", crumb: "nav.dashboard" },
  beforeLoad: ({ context }) => {
    if (context.platform === "android") throw redirect({ to: "/cashier" });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(outletsQuery),
  component: Home,
});

const useDashboardMessages = () => useMessages(dashboard);
type DashboardT = ReturnType<typeof useDashboardMessages>["t"];
type Format = ReturnType<typeof useDashboardMessages>["format"];

const ALL = "all";
type Period = "today" | "week" | "month";
const PERIODS: Period[] = ["today", "week", "month"];
const PERIOD_DAYS: Record<Period, number> = { today: 1, week: 7, month: 30 };

interface Filters {
  outletId: string;
  period: Period;
}

/** A period's business dates, ending today on the outlet's own clock and cutoff. */
function periodRange(period: Period, outlet: OutletSummary | undefined) {
  const today = businessDate(
    new Date(),
    outlet?.timezone ?? "Asia/Jakarta",
    outlet?.businessDayCutoff ?? "04:00",
  );
  return { from: addDays(today, 1 - PERIOD_DAYS[period]), to: today };
}

/** Read live; refreshed every minute while the page is open, so the freshness line stays honest. */
const dashboardQuery = (outletId: string, range: { from: string; to: string }) =>
  queryOptions({
    queryKey: ["reports", "dashboard", outletId, range],
    queryFn: async () =>
      unwrap(
        await api.v1.reports.dashboard.get({
          query: { ...range, ...(outletId === ALL ? {} : { outletId }) },
        }),
      ),
    // No placeholder from another filter: a card never shows figures under the wrong timeframe.
    refetchInterval: 60_000,
  });

/** The signed-in home: the owner's dashboard for whoever reports on sales, else a welcome. */
function Home() {
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  return canAnywhere(scopes, "reports.operational") ? <OwnerDashboard /> : <Welcome />;
}

function useGreeting() {
  const { t } = useDashboardMessages();
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const firstName = scopes.user.displayName.trim().split(/\s+/)[0] ?? scopes.user.displayName;
  return t("dashboard.greeting", { name: firstName });
}

/**
 * The owner's dashboard (R1-44, PRD §15): the sales report's headline figures over a period of business
 * days against as many days before it, payment and channel mix, cash variance, live low stock and the
 * sync and settlement exceptions. Every card states its timeframe, timezone and freshness. Phone-first:
 * one column at 360 px, more as the screen widens.
 */
function OwnerDashboard() {
  const { t } = useDashboardMessages();
  const greeting = useGreeting();
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const { outlet: current } = useCurrentOutlet();
  const reportable = outlets.filter((o) => canAt(scopes, "reports.operational", o.id));
  const [filters, setFilters] = useState<Filters>(() => ({
    outletId: reportable.length > 1 ? ALL : (reportable[0]?.id ?? ALL),
    period: "today",
  }));
  // Periods follow the chosen outlet's clock; across outlets, the current (or first) one's.
  const clockOutlet =
    reportable.find((o) => o.id === filters.outletId) ??
    reportable.find((o) => o.id === current?.id) ??
    reportable[0];
  const range = periodRange(filters.period, clockOutlet);
  const query = useQuery(dashboardQuery(filters.outletId, range));
  const data = query.data;

  return (
    <Page>
      <PageHeader
        title={t("dashboard.title")}
        subtitle={greeting}
        actions={
          <Button asChild variant="outline">
            <Link to="/reports">{t("dashboard.openReport")}</Link>
          </Button>
        }
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        {reportable.length > 1 && (
          // A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine).
          <OptionSelect
            aria-label={t("dashboard.outlet")}
            className="sm:w-56"
            options={[
              { value: ALL, label: t("dashboard.allOutlets") },
              ...reportable.map((o) => ({ value: o.id, label: o.name })),
            ]}
            value={filters.outletId}
            onValueChange={(outletId) => setFilters((f) => ({ ...f, outletId }))}
          />
        )}
        <OptionSelect
          aria-label={t("dashboard.period")}
          className="sm:w-48"
          options={PERIODS.map((p) => ({ value: p, label: t(`dashboard.period.${p}`) }))}
          value={filters.period}
          onValueChange={(period) => setFilters((f) => ({ ...f, period: period as Period }))}
        />
        <Button
          type="button"
          variant="outline"
          className="sm:ml-auto"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCwIcon className={query.isFetching ? "motion-safe:animate-spin" : undefined} />
          {t("dashboard.refresh")}
        </Button>
      </div>
      {query.isError && !data ? (
        <Card>
          <CardContent className="flex flex-col items-start gap-3">
            <p role="alert">{t("dashboard.loadFailed")}</p>
            <Button type="button" variant="outline" onClick={() => void query.refetch()}>
              {t("dashboard.retry")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Cards view={data} canReview={canAnywhere(scopes, "offline.reconcile")} />
      )}
    </Page>
  );
}

/**
 * What the figures cover and when they were read (PRD §15). The filters, timezone and freshness are the
 * same for every card, so they are said once under the filters; each section names its own timeframe.
 */
function useMeta(view: OwnerDashboardView | undefined) {
  const { t, format } = useDashboardMessages();
  if (!view) return null;
  const first = view.outlets[0];
  const zones = new Set(view.outlets.map((o) => `${o.timezone} ${o.businessDayCutoff}`));
  const days = (from: string | Date, to: string | Date) =>
    isoDay(from) === isoDay(to)
      ? t("dashboard.meta.day", { date: format.date(calendarDay(from)) })
      : t("dashboard.meta.days", { from: format.date(calendarDay(from)), to: format.date(calendarDay(to)) });
  const zone =
    zones.size === 1 && first
      ? t("dashboard.meta.zone", { zone: first.timezone, cutoff: first.businessDayCutoff })
      : t("dashboard.meta.zones");
  // Read just now, so the time of day says enough; the timeframe already names the days.
  const fresh = t("dashboard.meta.fresh", {
    time: format.time(isoInstant(view.generatedAt), first?.timezone),
  });
  // The outlet filter: the one outlet by name, else how many the figures span.
  const scope =
    view.outlets.length === 1 && first
      ? first.name
      : t("dashboard.meta.outlets", { count: view.outlets.length });
  const period = days(view.from, view.to);
  return {
    period,
    comparison: days(view.comparison.from, view.comparison.to),
    context: (
      <>
        {scope} · {zone} · <time dateTime={isoInstant(view.generatedAt)}>{fresh}</time>
      </>
    ),
    attention: t("dashboard.meta.attention", { period }),
  };
}

/** How a figure moved against the comparison range; null when that range had no sales (PRD §15). */
interface Delta {
  direction: "up" | "down" | "same";
  percent: string;
  /** The whole movement in words, for screen readers: the pill shows only the arrow and percent. */
  sentence: string;
}

function delta(
  t: DashboardT,
  format: Format,
  current: bigint,
  previous: bigint | null,
  period: string,
): Delta | null {
  // No sales then (or none worth a ratio): no growth figure is made up.
  if (previous === null || previous === 0n) return null;
  const ratio = Number(current - previous) / Number(previous);
  const percent = format.percent(Math.abs(ratio));
  if (percent === format.percent(0))
    return { direction: "same", percent, sentence: t("dashboard.same", { period }) };
  return ratio > 0
    ? { direction: "up", percent, sentence: t("dashboard.up", { percent, period }) }
    : { direction: "down", percent, sentence: t("dashboard.down", { percent, period }) };
}

/** A section's heading with the timeframe its figures cover. */
function SectionHeading(props: { id: string; title: string; caption: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h2 id={props.id} className="font-semibold text-lg tracking-tight">
        {props.title}
      </h2>
      <p className="text-muted-foreground text-sm">{props.caption}</p>
    </div>
  );
}

function Cards(props: { view: OwnerDashboardView | undefined; canReview: boolean }) {
  const { t, format } = useDashboardMessages();
  const { view } = props;
  const meta = useMeta(view);
  const loading = !view;
  const money = (value: string | undefined) => format.money(BigInt(value ?? "0"));
  const previous = (pick: (s: DashboardSales) => string | number) =>
    view?.previous ? BigInt(pick(view.previous)) : null;
  const change = (pick: (s: DashboardSales) => string | number) =>
    view && meta ? delta(t, format, BigInt(pick(view.sales)), previous(pick), meta.comparison) : null;
  const margin = view?.estimatedMargin;

  const figures: Figure[] = [
    {
      icon: <WalletIcon />,
      tone: "primary",
      label: t("dashboard.net"),
      value: money(view?.sales.net),
      delta: change((s) => s.net),
    },
    {
      icon: <ReceiptTextIcon />,
      tone: "secondary",
      label: t("dashboard.orders"),
      value: t("dashboard.ordersValue", { count: view?.sales.orders ?? 0 }),
      delta: change((s) => s.orders),
    },
    {
      icon: <ShoppingBasketIcon />,
      tone: "copper",
      label: t("dashboard.average"),
      value: money(view?.sales.averageTicket),
      delta: change((s) => s.averageTicket),
    },
  ];
  if (margin)
    figures.push({
      icon: <PiggyBankIcon />,
      tone: "success",
      label: t("dashboard.margin"),
      value: money(margin.margin),
      delta: null,
      note:
        margin.uncostedOrders === 0
          ? t("dashboard.marginNoteAll")
          : t("dashboard.marginNote", { count: margin.uncostedOrders }),
    });

  return (
    <div className="flex flex-col gap-6">
      {meta?.context && (
        <p className="-mt-2 flex items-center gap-2 text-muted-foreground text-sm">
          <ClockIcon aria-hidden="true" className="size-4 shrink-0" />
          <span>{meta.context}</span>
        </p>
      )}
      <section aria-labelledby="dashboard-sales" className="flex flex-col gap-3">
        <SectionHeading
          id="dashboard-sales"
          title={t("dashboard.sales")}
          caption={meta ? `${meta.period} · ${t("dashboard.vs", { period: meta.comparison })}` : null}
        />
        <FigureGrid
          figures={figures}
          loading={loading}
          // A day names the day before; a range is already named in the caption above.
          comparison={
            meta && view && isoDay(view.from) === isoDay(view.to)
              ? t("dashboard.vs", { period: meta.comparison })
              : t("dashboard.vsPrevious")
          }
        />
      </section>
      {/* On a phone the order is the trend, what needs attention, best sellers, then the mixes. On a wide
          screen attention and best sellers run beside the trend and the mixes as a rail. */}
      <div className="grid items-start gap-4 xl:grid-cols-3 xl:grid-rows-[auto_1fr]">
        <TrendCard view={view} caption={meta?.period} className="min-w-0 xl:col-span-2" />
        <div className="flex min-w-0 flex-col gap-4 xl:col-start-3 xl:row-span-2 xl:row-start-1">
          <section aria-labelledby="dashboard-attention" className="flex flex-col gap-3">
            <AttentionCard view={view} canReview={props.canReview} caption={meta?.attention} />
          </section>
          <TopProductsCard products={view?.topProducts} />
        </div>
        <section
          aria-label={t("dashboard.details")}
          className="grid min-w-0 gap-4 md:grid-cols-2 xl:col-span-2"
        >
          <MixCard
            icon={<CreditCardIcon />}
            title={t("dashboard.payments")}
            slices={view?.paymentMix}
            label={(key) =>
              t(`dashboard.payment.${key as "cash" | "qris" | "manual_qris_static" | "marketplace"}`)
            }
          />
          <MixCard
            icon={<StoreIcon />}
            title={t("dashboard.channels")}
            slices={view?.channels}
            label={(key) => t(`dashboard.channel.${key as "pos" | "grabfood" | "gofood" | "shopeefood"}`)}
            summary={(slices, total) => {
              const online = slices
                .filter((s) => s.key !== "pos")
                .reduce((sum, s) => sum + BigInt(s.amount), 0n);
              return t("dashboard.online", { percent: format.percent(Number(online) / Number(total)) });
            }}
          />
        </section>
      </div>
    </div>
  );
}

interface Figure {
  icon: ReactNode;
  tone: Tone;
  label: string;
  value: ReactNode;
  delta: Delta | null;
  /** Said instead of the movement, when the figure has none to compare (the margin is an estimate). */
  note?: ReactNode;
}

/**
 * The headline figures. Net sales leads in Forest, the brand's primary; the rest sit on cards with a
 * tinted icon. On a phone net sales has its own row, and an odd last figure closes on its own row too.
 */
function FigureGrid(props: { figures: Figure[]; loading: boolean; comparison: string }) {
  const { t } = useDashboardMessages();
  const count = props.figures.length;
  return (
    <div className={cn("grid grid-cols-2 gap-3 sm:gap-4", count === 4 ? "xl:grid-cols-4" : "lg:grid-cols-3")}>
      {props.figures.map((figure, index) => (
        <FigureCard
          key={figure.label}
          icon={figure.icon}
          tone={figure.tone}
          label={figure.label}
          value={figure.value}
          featured={index === 0}
          loading={props.loading}
          className={figureSpan(index, count, count === 4 ? "xl:col-span-1" : "lg:col-span-1")}
          footer={
            figure.delta ? (
              <>
                <DeltaPill delta={figure.delta} onBrand={index === 0} />
                <span aria-hidden="true">{props.comparison}</span>
                <span className="sr-only">{figure.delta.sentence}</span>
              </>
            ) : (
              <span>{figure.note ?? t("dashboard.noComparison")}</span>
            )
          }
        />
      ))}
    </div>
  );
}

/** The movement as an arrow and a percent; the words beside it (and for screen readers) carry it too. */
function DeltaPill(props: { delta: Delta; onBrand: boolean }) {
  const { direction, percent } = props.delta;
  const Icon = direction === "up" ? ArrowUpRightIcon : direction === "down" ? ArrowDownRightIcon : MinusIcon;
  const tone = props.onBrand
    ? "bg-primary-foreground/15 text-primary-foreground"
    : direction === "up"
      ? "bg-success/12 text-success"
      : direction === "down"
        ? "bg-destructive/10 text-destructive"
        : "bg-muted text-muted-foreground";
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full py-0.5 ps-1.5 pe-2 font-semibold text-xs tabular-nums",
        tone,
      )}
    >
      <Icon className="size-3.5 shrink-0" />
      {percent}
    </span>
  );
}

/** Every bucket of the range, sales or not, in time order: the outlet's hours, or each business day. */
function trendBuckets(view: OwnerDashboardView, t: DashboardT, locale: string): ChartBucket[] {
  const points = view.trend.points.map((p) => ({ ...p, amount: p.net }));
  if (view.trend.unit === "hour") {
    const hh = (hour: number) => t("dashboard.trend.hour", { hour: String(hour % 24).padStart(2, "0") });
    return hourBuckets(points, view.outlets[0]?.businessDayCutoff ?? "04:00", hh, (hour) =>
      t("dashboard.trend.hourRange", { from: hh(hour), to: hh(hour + 1) }),
    );
  }
  // The API client hands date-like strings back as Dates; a day's key is compared as `YYYY-MM-DD`.
  const byDay = new Map(points.map((p) => [isoDay(p.key), p]));
  const from = isoDay(view.from);
  const days = reportDays(from, isoDay(view.to));
  const tick = new Intl.DateTimeFormat(
    locale,
    days <= 7 ? { weekday: "short" } : { day: "numeric", month: "short" },
  );
  const long = new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" });
  return Array.from({ length: days }, (_, i) => {
    const date = addDays(from, i);
    const point = byDay.get(date);
    return {
      key: date,
      tick: tick.format(calendarDay(date)),
      label: long.format(calendarDay(date)),
      orders: point?.orders ?? 0,
      amount: BigInt(point?.amount ?? "0"),
    };
  });
}

/** Net sales over the period as columns: by hour for a day, by business day for a range. */
function TrendCard(props: { view: OwnerDashboardView | undefined; caption: ReactNode; className?: string }) {
  const { t, format, locale } = useDashboardMessages();
  const view = props.view;
  const intl = INTL_LOCALE[locale];
  const hourly = view?.trend.unit === "hour";
  return (
    <PanelCard
      icon={<ChartColumnIcon />}
      title={t("dashboard.trend")}
      caption={
        <>
          {hourly ? t("dashboard.trend.byHour") : t("dashboard.trend.byDay")}
          {props.caption ? ` · ${props.caption}` : ""}
        </>
      }
      className={cn("h-auto", props.className)}
    >
      <ColumnChart
        buckets={view ? trendBuckets(view, t, intl) : undefined}
        money={format.money}
        locale={intl}
        text={{
          peak: hourly ? t("dashboard.trend.peakHour") : t("dashboard.trend.peakDay"),
          empty: t("dashboard.noSales"),
          caption: t("dashboard.trend"),
          when: t("dashboard.trend.when"),
          amount: t("dashboard.net"),
          orders: t("dashboard.orders"),
          ordersValue: (count) => t("dashboard.ordersValue", { count }),
        }}
      />
    </PanelCard>
  );
}

/** The period's best sellers by net sales, each against the first, with how many were sold. */
function TopProductsCard(props: { products: DashboardTopProduct[] | undefined }) {
  const { t, format } = useDashboardMessages();
  const products = props.products;
  return (
    <PanelCard icon={<TrophyIcon />} title={t("dashboard.topProducts")} className="h-auto">
      <div className="flex flex-col gap-4">
        <RankedList
          items={products?.map((p) => ({
            key: p.productId,
            label: p.name,
            amount: BigInt(p.net),
            note: t("dashboard.sold", { count: p.quantity }),
          }))}
          money={format.money}
          empty={t("dashboard.noSales")}
        />
        {products && products.length > 0 && (
          <Button asChild variant="outline" size="sm" className="self-start">
            <Link to="/reports">{t("dashboard.seeAll")}</Link>
          </Button>
        )}
      </div>
    </PanelCard>
  );
}

/** A mix as one stacked bar and a legend: each slice's share in words and numbers, never by colour alone. */
function MixCard(props: {
  icon: ReactNode;
  title: string;
  slices: DashboardShare[] | undefined;
  label: (key: string) => string;
  summary?: (slices: Slice[], total: bigint) => string;
}) {
  const { t, format } = useDashboardMessages();
  return (
    <PanelCard icon={props.icon} title={props.title}>
      <MixBreakdown
        slices={props.slices?.map((s) => ({
          key: s.key,
          label: props.label(s.key),
          orders: s.orders,
          amount: BigInt(s.amount),
        }))}
        detail={(slice) => t("dashboard.slice", { count: slice.orders, amount: format.money(slice.amount) })}
        percent={format.percent}
        empty={t("dashboard.noSales")}
        {...(props.summary ? { summary: props.summary } : {})}
      />
    </PanelCard>
  );
}

/** One topic in the attention panel: a tinted icon when it needs action, its status, then the detail. */
function AttentionItem(props: {
  icon: ReactNode;
  title: string;
  needsAction: boolean;
  status?: ReactNode;
  children: ReactNode;
}) {
  return (
    <li className="flex gap-3 py-4 first:pt-0 last:pb-0">
      <span
        className={`inline-flex size-9 shrink-0 items-center justify-center rounded-lg [&_svg]:size-[18px] ${
          props.needsAction ? "bg-warning/15 text-warning" : "bg-muted text-primary"
        }`}
      >
        {props.icon}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-h-9 items-center justify-between gap-3">
          <h3 className="font-medium">{props.title}</h3>
          {props.status}
        </div>
        <div className="flex flex-col gap-2 text-muted-foreground text-sm">{props.children}</div>
      </div>
    </li>
  );
}

/** Cash variance, low stock and the sync and settlement exceptions: what may need the owner today. */
function AttentionCard(props: {
  view: OwnerDashboardView | undefined;
  canReview: boolean;
  caption: ReactNode;
}) {
  const { t, format } = useDashboardMessages();
  const view = props.view;
  const money = (value: string) => format.money(BigInt(value));
  const zone = view?.outlets[0]?.timezone;
  const several = (view?.outlets.length ?? 0) > 1;

  if (!view)
    return (
      <Card className="py-5">
        <CardContent className="px-5">
          <ListSkeleton />
        </CardContent>
      </Card>
    );

  const { cash, lowStock: low, exceptions: x } = view;
  const variance = BigInt(cash.variance);
  const lines: ReactNode[] = [];
  if (x.offlineRegisters.length > 0)
    lines.push(
      <li key="offline" className="flex flex-col gap-1 py-2">
        <span className="text-foreground">
          {t("dashboard.offlineRegisters", { count: x.offlineRegisters.length })}
        </span>
        <ul className="flex flex-col gap-0.5">
          {x.offlineRegisters.map((r) => (
            <li key={r.registerId}>
              {t("dashboard.offlineRegister", {
                register: r.registerName,
                outlet: r.outletName,
                synced: r.lastSyncedAt
                  ? format.dateTime(isoInstant(r.lastSyncedAt), zone)
                  : t("dashboard.neverSynced"),
              })}
            </li>
          ))}
        </ul>
      </li>,
    );
  if (x.quarantined > 0)
    lines.push(
      <li key="quarantined" className="flex flex-wrap items-center justify-between gap-2 py-2">
        <span className="text-foreground">{t("dashboard.quarantined", { count: x.quarantined })}</span>
        {props.canReview && (
          <Button asChild variant="outline" size="sm">
            <Link to="/offline-exceptions" search={{ status: "open" }}>
              {t("dashboard.review")}
            </Link>
          </Button>
        )}
      </li>,
    );
  const tallies = [
    [x.awaitingPayment, "dashboard.awaiting"],
    [x.pendingRefunds, "dashboard.pendingRefunds"],
    [x.marketplaceReceivable, "dashboard.receivable"],
  ] as const;
  for (const [tally, key] of tallies)
    if (tally.count > 0)
      lines.push(
        <li key={key} className="py-2 text-foreground">
          {t(key, { count: tally.count, amount: money(tally.amount) })}
        </li>,
      );
  const lowCount = low?.total ?? 0;

  return (
    <Card className="gap-4 py-5">
      <CardHeader className="flex flex-col gap-0.5 px-5">
        <h2 id="dashboard-attention" className="font-semibold text-base">
          {t("dashboard.attention")}
        </h2>
        <p className="text-muted-foreground text-sm">{props.caption}</p>
      </CardHeader>
      <CardContent className="px-5">
        <ul className="flex flex-col divide-y">
          <AttentionItem
            icon={<ScaleIcon />}
            title={t("dashboard.cash")}
            needsAction={variance !== 0n}
            status={<span className="font-semibold tabular-nums">{money(cash.variance)}</span>}
          >
            <p>
              {variance === 0n && cash.shiftsClosed > 0 ? `${t("dashboard.cashClear")} · ` : ""}
              {t("dashboard.cashNote", { count: cash.shiftsClosed, open: cash.shiftsUnclosed })}
            </p>
          </AttentionItem>
          {low !== null && (
            <AttentionItem
              icon={<PackageIcon />}
              title={t("dashboard.lowStock")}
              needsAction={lowCount > 0}
              status={
                lowCount > 0 && (
                  <Badge variant="warning">{t("dashboard.lowStockValue", { count: lowCount })}</Badge>
                )
              }
            >
              {lowCount === 0 ? (
                <p>{t("dashboard.lowStockClear")}</p>
              ) : (
                <>
                  <ul className="flex flex-col divide-y">
                    {low.items.map((item) => (
                      <li
                        key={`${item.outletId}:${item.ingredientId}`}
                        className="flex flex-col gap-0.5 py-2 first:pt-0"
                      >
                        <span className="font-medium text-foreground">{item.ingredientName}</span>
                        {several && <span>{item.outletName}</span>}
                        <span className="tabular-nums">
                          {t("dashboard.lowStockItem", {
                            available: format.quantity(item.available),
                            threshold: format.quantity(item.threshold),
                            unit: item.baseUnit,
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {low.total > low.items.length && (
                    <p>{t("dashboard.lowStockMore", { count: low.total - low.items.length })}</p>
                  )}
                  <Button asChild variant="outline" size="sm" className="self-start">
                    <Link to="/stock">{t("dashboard.openStock")}</Link>
                  </Button>
                </>
              )}
            </AttentionItem>
          )}
          <AttentionItem
            icon={<MonitorSmartphoneIcon />}
            title={t("dashboard.exceptions")}
            needsAction={lines.length > 0}
            status={lines.length > 0 && <Badge variant="warning">{lines.length}</Badge>}
          >
            {lines.length === 0 ? (
              <p>{t("dashboard.exceptionsClear")}</p>
            ) : (
              <ul className="flex flex-col divide-y">{lines}</ul>
            )}
          </AttentionItem>
        </ul>
      </CardContent>
    </Card>
  );
}

type ShortcutTo = "/catalogue" | "/staff" | "/devices" | "/settings";

interface Shortcut {
  to: ShortcutTo;
  icon: ReactNode;
  title: string;
  description: string;
}

/** The home for who doesn't report on sales: their outlets and where to start. */
function Welcome() {
  const { t } = useDashboardMessages();
  const greeting = useGreeting();
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { outlets } = useCurrentOutlet();
  const activeOutlets = outlets.filter((o) => o.active).length;
  const inactiveOutlets = outlets.length - activeOutlets;

  const shortcuts: Shortcut[] = [];
  if (canAnywhere(scopes, "catalogue.manage"))
    shortcuts.push({
      to: "/catalogue",
      icon: <BookOpenIcon />,
      title: t("nav.catalogue"),
      description: t("dashboard.catalogueHint"),
    });
  if (canTenantWide(scopes, "staff.manage"))
    shortcuts.push({
      to: "/staff",
      icon: <UsersIcon />,
      title: t("nav.staff"),
      description: t("dashboard.staffHint"),
    });
  if (canAnywhere(scopes, "devices.manage"))
    shortcuts.push({
      to: "/devices",
      icon: <MonitorSmartphoneIcon />,
      title: t("nav.devices"),
      description: t("dashboard.devicesHint"),
    });
  if (canOpenSettings(scopes))
    shortcuts.push({
      to: "/settings",
      icon: <SettingsIcon />,
      title: t("nav.settings"),
      description: t("dashboard.settingsHint"),
    });

  return (
    <Page>
      <PageHeader title={t("dashboard.title")} subtitle={greeting} />
      <section aria-label={t("dashboard.today")} className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={<StoreIcon />}
          label={t("dashboard.activeOutlets")}
          value={t("outlet.count", { count: activeOutlets })}
          note={
            inactiveOutlets === 0
              ? t("dashboard.allOutletsActive")
              : t("dashboard.inactiveOutlets", { count: inactiveOutlets })
          }
        />
      </section>
      {shortcuts.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="font-semibold text-lg tracking-tight">{t("dashboard.startHere")}</h2>
          <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {shortcuts.map((s) => (
              <li key={s.to}>
                <Link
                  to={s.to}
                  className="group block h-full rounded-xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  <Card className="h-full flex-row items-center gap-3 px-5 py-4 transition-colors duration-150 group-hover:border-primary/40 motion-reduce:transition-none">
                    <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-primary [&_svg]:size-5">
                      {s.icon}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="font-semibold">{s.title}</span>
                      <span className="text-muted-foreground text-sm">{s.description}</span>
                    </span>
                    <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Page>
  );
}
